import {
  BarChart3,
  PieChart,
  Table2,
  LineChart,
  Type,
  ChevronRight,
  ChevronDown,
  ArrowUp,
  Check,
  Loader2,
  Table,
  Columns,
} from "lucide-react"
import { useState, useRef, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle } from "react"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "./button"
import { useToast } from "@/hooks/useToast"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useListRenderOptions } from "@/hooks/useChat"
import { useMentionOptions } from "@/hooks/useMentionOptions"
import { RenderOptionType } from "@/types/api"

const ICONS: Record<string, any> = { Table2, BarChart3, PieChart, LineChart }
const getIcon = (name?: string) => {
  if (!name) return Table2
  return ICONS[name] || Table2
}

interface MultimodalInputProps {
  onSubmit: (message: string, action?: string) => void
  value?: string
  setValue?: (v: string) => void
  inputRef?: React.MutableRefObject<{
    focus: () => void
    setValue: (value: string) => void
    getValue: () => string
  } | null>
  isEditMode?: boolean
  onCancel?: () => void
  placeholder?: string
  /** Rendered first in the bottom control bar (e.g. resource selector). */
  controlBarStart?: React.ReactNode
  /** When set, typing @ opens a dropdown of resource tables/columns to insert as mentions. */
  selectedResourceId?: string | null
  containerClassName?: string
  popoverClassName?: string
  isSubmitting?: boolean
  clearOnSubmit?: boolean
  hideControlBarSpacer?: boolean
  textAreaClassName?: string
  /** Allow @-mention dropdown even when `isEditMode` is true. */
  allowMentionsInEditMode?: boolean
}

export const MultimodalInput = forwardRef(function MultimodalInput(
  {
    onSubmit,
    value,
    setValue,
    inputRef,
    isEditMode = false,
    onCancel,
    placeholder,
    controlBarStart,
    selectedResourceId,
    containerClassName,
    popoverClassName,
    isSubmitting = false,
    clearOnSubmit = true,
    hideControlBarSpacer = false,
    textAreaClassName,
    allowMentionsInEditMode = false,
  }: MultimodalInputProps,
  _ref
) {
  const { toast } = useToast()
  const [internalValue, setInternalValue] = useState("")
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const isControlled = typeof value === "string" && typeof setValue === "function"
  const inputValue = isControlled ? value : internalValue
  const setInputValue = isControlled ? setValue! : setInternalValue
  const [selectedAction, setSelectedAction] = useState<string | null>(null)
  const [optionModalOpen, setOptionModalOpen] = useState(false)
  const [optionModalTitle, setOptionModalTitle] = useState<string>("")
  const [optionModalItems, setOptionModalItems] = useState<RenderOptionType[]>([])
  const { data: renderOptions = [] } = useListRenderOptions()

  // @-mention: options from selected resource (tables, columns)
  const mentionOptionsAll = useMentionOptions(selectedResourceId ?? null)
  const [mentionOpen, setMentionOpen] = useState(false)
  const [mentionQuery, setMentionQuery] = useState("")
  const [mentionStart, setMentionStart] = useState(0)
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0)

  const mentionFiltered = useMemo(() => {
    if (!mentionQuery.trim()) return mentionOptionsAll
    const q = mentionQuery.toLowerCase()
    return mentionOptionsAll.filter((item) => item.label.toLowerCase().includes(q))
  }, [mentionOptionsAll, mentionQuery])

  useEffect(() => {
    if (mentionOpen && mentionFiltered.length > 0) {
      setMentionSelectedIndex((i) => Math.min(i, mentionFiltered.length - 1))
    }
  }, [mentionOpen, mentionFiltered.length])

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const v = e.target.value
      const cursor = e.target.selectionStart ?? v.length
      setInputValue(v)

      if (!selectedResourceId || (isEditMode && !allowMentionsInEditMode)) {
        setMentionOpen(false)
        return
      }
      const textBeforeCursor = v.slice(0, cursor)
      const lastAt = textBeforeCursor.lastIndexOf("@")
      if (lastAt === -1) {
        setMentionOpen(false)
        return
      }
      const afterAt = textBeforeCursor.slice(lastAt + 1)
      if (/\s/.test(afterAt)) {
        setMentionOpen(false)
        return
      }
      setMentionOpen(true)
      setMentionStart(lastAt)
      setMentionQuery(afterAt)
      setMentionSelectedIndex(0)
    },
    [selectedResourceId, isEditMode, allowMentionsInEditMode, setInputValue]
  )

  const insertMention = useCallback(
    (item: { insert: string }) => {
      const ta = textareaRef.current
      if (!ta) return
      const start = mentionStart
      const end = ta.selectionStart
      const before = inputValue.slice(0, start)
      const after = inputValue.slice(end)
      const newValue = before + item.insert + " " + after
      setInputValue(newValue)
      setMentionOpen(false)
      requestAnimationFrame(() => {
        ta.focus()
        const pos = start + item.insert.length + 1
        ta.setSelectionRange(pos, pos)
      })
    },
    [inputValue, mentionStart, setInputValue]
  )

  const handleMentionKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!mentionOpen || mentionFiltered.length === 0) return
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setMentionSelectedIndex((i) => (i + 1) % mentionFiltered.length)
        return
      }
      if (e.key === "ArrowUp") {
        e.preventDefault()
        setMentionSelectedIndex((i) => (i - 1 + mentionFiltered.length) % mentionFiltered.length)
        return
      }
      if (e.key === "Enter" && mentionFiltered[mentionSelectedIndex]) {
        e.preventDefault()
        insertMention(mentionFiltered[mentionSelectedIndex])
        return
      }
      if (e.key === "Escape") {
        setMentionOpen(false)
      }
    },
    [mentionOpen, mentionFiltered, mentionSelectedIndex, insertMention]
  )

  // Parse input into segments for overlay (plain text vs @mention)
  const mentionSegments = useMemo(() => {
    return inputValue.split(/(@[\w.]+)/g)
  }, [inputValue])

  // Only highlight segments that are actual selected mentions (from dropdown options)
  const validMentionInserts = useMemo(() => new Set(mentionOptionsAll.map((m) => m.insert)), [mentionOptionsAll])

  // Sync overlay scroll with textarea
  const handleTextareaScroll = useCallback(() => {
    const ta = textareaRef.current
    const ov = overlayRef.current
    if (ta && ov) {
      ov.scrollTop = ta.scrollTop
      ov.scrollLeft = ta.scrollLeft
    }
  }, [])

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "inherit"
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`
    }
  }, [inputValue])

  // Expose focus, setValue, and getValue methods to parent
  useImperativeHandle(
    inputRef,
    () => ({
      focus: () => {
        textareaRef.current?.focus()
      },
      setValue: (value: string) => {
        setInputValue(value)
      },
      getValue: () => {
        return inputValue
      },
    }),
    [setInputValue, inputValue]
  )

  const handleActionSelect = (actionText: string | null) => {
    if (actionText === null) {
      setSelectedAction(null)
      return
    }
    const action = renderOptions.find((action) => action.text === actionText)

    if (!action?.available) {
      toast({
        description: `${actionText} feature is coming soon!`,
      })
      return
    }

    if (action?.options && action.options.length > 0) {
      setOptionModalTitle(action.text)
      setOptionModalItems(action.options)
      setOptionModalOpen(true)
      return
    }

    setSelectedAction(actionText)
  }

  // Resolve the currently selected quick action and label (handles options like bar.simple)
  let currentAction: any = null
  let currentActionLabel: string | null = null
  if (selectedAction) {
    const byText = renderOptions.find((action: any) => action.text === selectedAction)
    if (byText) {
      currentAction = byText
      currentActionLabel = byText.text
    } else {
      const withOption = renderOptions.find(
        (action: any) => Array.isArray(action.options) && action.options?.some((opt: any) => opt.id === selectedAction)
      )
      if (withOption) {
        currentAction = withOption
        const opt = (withOption.options || []).find((opt: any) => opt.id === selectedAction)
        currentActionLabel = opt?.label || withOption.text
      }
    }
  }

  const handleSubmit = () => {
    if (!inputValue.trim() || isSubmitting) return
    onSubmit(inputValue, selectedAction ?? undefined)
    if (clearOnSubmit) {
      setInputValue("")
      setSelectedAction(null)
      if (textareaRef.current) {
        textareaRef.current.style.height = "52px"
      }
    }
  }

  return (
    <div className="w-full">
      <div className="relative mx-auto w-full">
        {/* Single container: textarea on top, control bar on bottom (like reference) */}
        <div
          className={cn(
            "overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md dark:border-gray-700 dark:bg-gray-800/95",
            containerClassName
          )}
        >
          {/* Top: text input area with ample space */}
          <div className={cn("relative px-4 pt-4", popoverClassName)}>
            <Popover
              open={mentionOpen && !!selectedResourceId && (!isEditMode || allowMentionsInEditMode)}
              onOpenChange={(open) => !open && setMentionOpen(false)}
            >
              <PopoverTrigger asChild>
                <div className="relative w-full" style={{ minHeight: "1.5rem" }}>
                  {/* Overlay: same content with @mentions styled (blue/italic) */}
                  {inputValue && (
                    <div
                      ref={overlayRef}
                      aria-hidden
                      className="pointer-events-none absolute inset-0 overflow-auto whitespace-pre-wrap break-words px-0 pb-1 pt-2 font-[inherit] text-sm leading-[1.5] text-gray-900 dark:text-gray-100"
                      style={{ minHeight: "20px" }}
                    >
                      {mentionSegments.map((part, i) =>
                        part.startsWith("@") && /^@[\w.]+$/.test(part) && validMentionInserts.has(part) ? (
                          <span key={i} className="text-violet-600 dark:text-violet-400">
                            {part}
                          </span>
                        ) : (
                          <span key={i}>{part}</span>
                        )
                      )}
                    </div>
                  )}
                  <Textarea
                    ref={textareaRef}
                    placeholder={placeholder || "Ask anything about your data, use @ to mention tables or columns."}
                    className={cn(
                      "relative z-10 min-h-[20px] w-full resize-none border-none bg-transparent px-0 pb-1 pt-2 text-sm leading-[1.5] placeholder:text-gray-500 focus-visible:ring-0 focus-visible:ring-offset-0 dark:placeholder:text-gray-400",
                      "max-h-[150px]",
                      inputValue
                        ? "text-transparent caret-gray-900 dark:caret-gray-100"
                        : "text-gray-900 dark:text-gray-100",
                      textAreaClassName
                    )}
                    readOnly={isSubmitting}
                    rows={2}
                    value={inputValue}
                    onChange={handleInputChange}
                    onScroll={handleTextareaScroll}
                    onKeyDown={(e) => {
                      handleMentionKeyDown(e)
                      if (e.defaultPrevented) return
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault()
                        if (!isSubmitting) handleSubmit()
                      }
                    }}
                  />
                </div>
              </PopoverTrigger>
              <PopoverContent
                side="top"
                align="start"
                sideOffset={6}
                className="max-h-64 overflow-auto rounded-xl border border-gray-200 bg-white p-0 shadow-lg dark:border-gray-700 dark:bg-gray-800"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                {mentionFiltered.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">No matches</div>
                ) : (
                  <div className="py-1">
                    {mentionFiltered.slice(0, 50).map((item, i) => {
                      const Icon = item.kind === "table" ? Table : Columns
                      return (
                        <button
                          key={`${item.kind}-${item.insert}`}
                          type="button"
                          className={cn(
                            "flex w-full items-center gap-2 px-3 py-2 text-left text-xs",
                            i === mentionSelectedIndex
                              ? "bg-gray-100 dark:bg-gray-700"
                              : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                          )}
                          onMouseDown={(e) => {
                            e.preventDefault()
                            insertMention(item)
                          }}
                        >
                          <Icon className="h-3.5 w-3.5 shrink-0 text-gray-500 dark:text-gray-400" />
                          <span className="text-gray-800 dark:text-gray-200">{item.label}</span>
                          <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">({item.kind})</span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>

          {/* Bottom: control bar (optional start slot, render-option select, send) */}
          <div className="flex items-center gap-2 px-3 py-2 dark:border-gray-700/80">
            {controlBarStart != null ? controlBarStart : null}
            {!isEditMode && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-50 bg-gray-50/20 px-1.5 py-1.5 text-sm text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700/50 dark:text-gray-300 dark:hover:bg-gray-700"
                    aria-label="Choose output type"
                  >
                    {selectedAction ? (
                      <>
                        {(() => {
                          const Icon = getIcon(currentAction?.icon)
                          return <Icon className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
                        })()}
                        <span className="text-xs">{currentActionLabel}</span>
                      </>
                    ) : (
                      <>
                        <Type className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
                        <span className="text-xs">Text</span>
                      </>
                    )}
                    <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-56 rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
                >
                  <DropdownMenuItem
                    onClick={() => handleActionSelect(null)}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm"
                  >
                    <Type className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
                    <span className="flex-1 text-left text-xs text-gray-700 dark:text-gray-300">Text</span>
                    {!selectedAction && <Check className="h-4 w-4 shrink-0 text-gray-600 dark:text-gray-300" />}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {renderOptions
                    .filter((action) => !action.hidden)
                    .map((action) => {
                      const isSelected =
                        selectedAction === action.text ||
                        (Array.isArray(action.options) && action.options?.some((opt: any) => opt.id === selectedAction))
                      return (
                        <DropdownMenuItem
                          key={action.text}
                          onClick={() => handleActionSelect(action.text)}
                          className="flex items-center gap-3 px-3 py-2.5 text-xs"
                        >
                          {(() => {
                            const Icon = getIcon(action.icon)
                            return <Icon className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
                          })()}
                          <span className="flex-1 text-left text-gray-700 dark:text-gray-300">{action.text}</span>
                          {Array.isArray(action.options) && <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />}
                          {isSelected && <Check className="h-4 w-4 shrink-0 text-gray-600 dark:text-gray-300" />}
                        </DropdownMenuItem>
                      )
                    })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {isEditMode && onCancel && (
              <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSubmitting} className="h-8 text-xs">
                Cancel
              </Button>
            )}

            {!hideControlBarSpacer ? <div className="flex-1" /> : null}

            <Button
              type="button"
              size="icon"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={cn(
                "h-9 w-9 shrink-0 rounded-lg transition-colors",
                hideControlBarSpacer && "ml-auto",
                inputValue.trim() && !isSubmitting
                  ? "bg-black text-white hover:bg-black/90 dark:bg-white dark:text-black dark:hover:bg-white/90"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-gray-200"
              )}
              aria-label={isSubmitting ? "Generating" : "Send"}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} />
              ) : (
                <ArrowUp className="h-4 w-4" strokeWidth={2.5} />
              )}
            </Button>
          </div>
        </div>
        {/* Quick Action options modal */}
        <Dialog open={optionModalOpen} onOpenChange={setOptionModalOpen}>
          <DialogContent className="h-[100dvh] w-screen max-w-4xl overflow-y-auto overscroll-contain p-3 md:h-[min(90vw,90vh)] md:w-[min(95vw,1000px)] md:p-6">
            <div className="flex w-full flex-col items-start justify-start">
              <DialogHeader>
                <DialogTitle>Select a {optionModalTitle.toLowerCase()} style</DialogTitle>
              </DialogHeader>
              <div className="mt-6 w-full">
                <div className="grid grid-cols-1 content-start items-start justify-start gap-6 md:grid-cols-4">
                  {optionModalItems.map((opt) => (
                    <Button
                      key={opt.id}
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setSelectedAction(opt.id)
                        setOptionModalOpen(false)
                      }}
                      className="!flex !h-auto !w-full !flex-col !items-start !justify-start self-start rounded-lg border p-2 text-left shadow-md shadow-gray-100 hover:bg-gray-50 dark:hover:bg-zinc-800"
                    >
                      <div className="aspect-video w-full overflow-hidden rounded bg-gray-100">
                        {opt.img && <img src={opt.img} alt={opt.label} className="h-full w-full object-cover" />}
                      </div>
                      <div className="mt-2 text-sm font-medium">{opt.label}</div>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
})
